<template>
  <div v-if="files.length" class="space-y-1.5">
    <div
      v-for="(file, index) in files"
      :key="index"
      class="flex items-center bg-gray-50 rounded px-3 py-2"
    >
      <FontAwesomeIcon :icon="['fas', fileIcon(file)]" class="text-gray-400 mr-2 shrink-0" />
      <button
        v-if="isImage(file)"
        class="text-sm text-blue-600 hover:text-blue-800 truncate"
        @click="previewFile = file"
      >
        {{ fileName(file) }}
      </button>
      <button
        v-else
        class="text-sm text-blue-600 hover:text-blue-800 truncate"
        @click="download(file)"
      >
        {{ fileName(file) }}
      </button>
      <span class="text-xs text-gray-400 ml-2 shrink-0">{{ formatSize(file.file_size) }}</span>
      <button
        class="ml-auto text-gray-400 hover:text-gray-600 shrink-0 pl-2"
        :aria-label="`Download ${fileName(file)}`"
        title="Download"
        @click="download(file)"
      >
        <FontAwesomeIcon :icon="['fas', 'download']" class="text-xs" />
      </button>
      <!-- Only rendered where the caller knows the user can actually delete
           (the API gates destroy to admin/security). -->
      <button
        v-if="removable"
        class="text-gray-400 hover:text-red-500 shrink-0 pl-2"
        :aria-label="`Remove ${fileName(file)}`"
        title="Remove"
        @click="$emit('remove', file)"
      >
        <FontAwesomeIcon :icon="['fas', 'times']" class="text-xs" />
      </button>
    </div>

    <!-- Image thumbnails. Rendered only once the bytes are in hand, so a
         thumbnail is never a broken-image icon. -->
    <div v-if="loadedImages.length" class="flex flex-wrap gap-2 mt-2">
      <button
        v-for="(img, i) in loadedImages"
        :key="'thumb-' + i"
        class="relative w-16 h-16 rounded border border-gray-200 overflow-hidden hover:border-primary-300 transition-colors"
        @click="previewFile = img"
      >
        <img
          :src="objectUrls[rowKey(img)]"
          :alt="fileName(img)"
          class="w-full h-full object-cover"
        />
      </button>
    </div>

    <p v-if="loadError" class="text-xs text-gray-400">{{ loadError }}</p>

    <!-- Image preview modal -->
    <Teleport to="body">
      <div
        v-if="previewFile"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
        @click="previewFile = null"
      >
        <div class="relative max-w-4xl max-h-[90vh] p-2" @click.stop>
          <button
            class="absolute -top-2 -right-2 bg-white rounded-full w-7 h-7 flex items-center justify-center shadow text-gray-500 hover:text-gray-800"
            @click="previewFile = null"
          >
            <FontAwesomeIcon :icon="['fas', 'times']" />
          </button>
          <img
            v-if="objectUrls[rowKey(previewFile)]"
            :src="objectUrls[rowKey(previewFile)]"
            :alt="fileName(previewFile)"
            class="max-w-full max-h-[85vh] rounded shadow-lg"
          />
          <div v-else class="bg-white rounded px-6 py-4 text-sm text-gray-500">
            Couldn't load this image.
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { beginSensitiveEgress, sensitiveEgressBlocked } from '@/utils/demoEgress';

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export default {
  name: 'RequirementAttachmentList',
  components: { FontAwesomeIcon },
  props: {
    files: { type: Array, default: () => [] },
    // Retained for callers that pass rows without a file_key; the bytes now
    // come through axios either way.
    useMediaEndpoint: { type: Boolean, default: false },
    removable: { type: Boolean, default: false },
  },
  emits: ['remove'],
  data() {
    return {
      previewFile: null,
      // file_key -> blob: URL. Attachments must be fetched through axios, not
      // linked directly: a raw <img src>/<a href> to /api/media/ carries no
      // tenant_slug (the request interceptor adds it) and no credentials
      // cross-origin, so it 403s. Object URLs also give downloads their real
      // filename instead of the storage UUID.
      objectUrls: {},
      loadError: '',
    };
  },
  computed: {
    egressBlocked() {
      return sensitiveEgressBlocked();
    },
    loadedImages() {
      return this.files.filter(
        (f) => this.isImage(f) && this.objectUrls[this.rowKey(f)]
      );
    },
  },
  watch: {
    files: { immediate: true, handler: 'loadImages' },
    egressBlocked: 'loadImages',
  },
  beforeUnmount() {
    this.revokeAll();
  },
  methods: {
    rowKey(file) {
      return file?.id ?? file?.file_key;
    },
    fileName(file) {
      return file.filename || file.file_name || 'attachment';
    },
    isImage(file) {
      const type = file.content_type || file.file_type || '';
      return IMAGE_TYPES.some((t) => type.includes(t));
    },
    // One transport for every attachment, deliberately. Reading the bytes any
    // other way needs something we do not have: a raw element URL carries no
    // tenant_slug (the axios interceptor adds it) and no cross-origin
    // credentials, and fetch()ing a presigned S3 URL needs bucket CORS — the
    // exact preflight that made uploads fail for two months. RequirementMedia
    // always has a file_key (non-null column, and create rejects without it),
    // so this needs no fallback; a row missing one is a data problem the
    // callers report rather than something to paper over.
    async fetchBlob(file) {
      if (!file.file_key) {
        throw new Error(`${this.fileName(file)} has no stored file.`);
      }
      const { data } = await axios.get('/api/media/', {
        params: { key: file.file_key },
        responseType: 'blob',
      });
      return data;
    },
    async loadImages() {
      this.revokeAll();
      this.loadError = '';
      const images = this.files.filter((f) => this.isImage(f) && this.rowKey(f));
      if (!images.length || sensitiveEgressBlocked()) return;
      const urls = {};
      let failed = 0;
      await Promise.all(
        images.map(async (file) => {
          try {
            urls[this.rowKey(file)] = URL.createObjectURL(await this.fetchBlob(file));
          } catch {
            failed += 1;
          }
        })
      );
      if (sensitiveEgressBlocked()) {
        Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
        return;
      }
      this.objectUrls = urls;
      if (failed) {
        this.loadError = `${failed} image${failed === 1 ? "" : 's'} couldn't be previewed. Use Download instead.`;
      }
    },
    async download(file) {
      const ticket = beginSensitiveEgress('download');
      if (!ticket) return;
      try {
        const blob = await this.fetchBlob(file);
        if (!ticket.confirm()) return;
        const url = URL.createObjectURL(blob);
        // A download-attributed anchor click isn't treated as a popup, so this
        // survives being triggered from an async handler.
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = this.fileName(file);
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
      } catch {
        this.loadError = `Couldn't download ${this.fileName(file)}.`;
      }
    },
    revokeAll() {
      Object.values(this.objectUrls).forEach((url) => URL.revokeObjectURL(url));
      this.objectUrls = {};
    },
    fileIcon(file) {
      const type = file.content_type || file.file_type || '';
      if (type.includes('pdf')) return 'file-pdf';
      if (type.includes('image')) return 'file-image';
      if (type.includes('word')) return 'file-word';
      if (type.includes('sheet') || type.includes('excel')) return 'file-excel';
      if (type.includes('presentation') || type.includes('powerpoint')) return 'file-powerpoint';
      return 'file';
    },
    formatSize(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },
  },
};
</script>
